import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Clock3, MapPin, Sparkles, Trophy } from 'lucide-react';
import ClubSelector from './ClubSelector';
import { eventsService } from '../../../../../../services';

const CreateEvent = ({ isOpen, closeModal, onEventCreated, initialEventType = 'Clay' }) => {
    const navigate = useNavigate();
    const [eventData, setEventData] = useState({
        name: '',
        clubId: '',
        eventDate: new Date().toISOString().split('T')[0],
        eventTime: new Date().toTimeString().slice(0, 5),
        eventType: initialEventType
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const eventTypeCards = useMemo(() => ([
        { value: 'Clay', title: 'Clay', subtitle: 'Sporting, Skeet, Trap and more' },
        { value: 'Rifle', title: 'Rifle', subtitle: 'Range and precision events' },
        { value: 'Game', title: 'Game', subtitle: 'Driven days and estate shoots' }
    ]), []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setEventData((prev) => ({
            ...prev,
            eventType: initialEventType || 'Clay'
        }));
    }, [initialEventType, isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEventData(prev => ({ ...prev, [name]: value }));
        setFormError('');
    };

    const handleClubSelect = (club) => {
        setEventData(prev => ({
            ...prev,
            clubId: club.clubId,
            clubName: club.name
        }));
        setFormError('');
    };

    const submitForm = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!eventData.clubId) {
            setFormError('Please select a venue before creating your event.');
            return;
        }

        setIsSubmitting(true);
        setFormError('');
        try {
            const createdEvent = await eventsService.createEvent(eventData);
            onEventCreated(createdEvent);
            const eventId = createdEvent?.eventId;
            const routeByType = {
                Clay: '/dashboard/score/clay',
                Rifle: '/dashboard/score/rifle',
                Game: '/dashboard/score/game'
            };
            const targetPath = routeByType[eventData.eventType] || '/dashboard/score';
            if (targetPath.includes('?')) {
                navigate(eventId ? `${targetPath}&eventId=${eventId}` : targetPath);
            } else {
                navigate(eventId ? `${targetPath}?eventId=${eventId}` : targetPath);
            }
            closeModal();
        } catch (error) {
            console.error('Error creating event:', error);
            setFormError(error?.message || 'We could not create this event yet. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="create-event-container active">
            <div className="create-event-overlay" onClick={closeModal}></div>
            <div className="create-event-box">
                <div className="modal-header">
                    <h2>Create Event</h2>
                    <p className="create-event-subtitle">Set the basics, pick your venue, then continue straight into scoring tools.</p>
                </div>
                <form onSubmit={submitForm}>
                    <div className="row create-event-step-row">
                        <div className="col-12">
                            <div className="create-event-step-heading">
                                <Sparkles size={16} />
                                <h4>1. Event basics</h4>
                            </div>
                            <div className="form-group">
                                <label className='dashboard-form-label'>Name</label>
                                <div className="dashboard-form-field">
                                    <input
                                        type="text"
                                        name="name"
                                        value={eventData.name}
                                        onChange={handleInputChange}
                                        placeholder='Event Name'
                                        className='create-event-input'
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="form-group">
                                <label className='dashboard-form-label'>Shooting Type</label>
                                <div className="create-event-type-grid">
                                    {eventTypeCards.map((option) => (
                                        <button
                                            type="button"
                                            key={option.value}
                                            className={`create-event-type-card ${eventData.eventType === option.value ? 'active' : ''}`}
                                            onClick={() => setEventData((prev) => ({ ...prev, eventType: option.value }))}
                                        >
                                            <strong>{option.title}</strong>
                                            <span>{option.subtitle}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row create-event-step-row">
                        <div className="col-12">
                            <div className="create-event-step-heading">
                                <MapPin size={16} />
                                <h4>2. Venue and schedule</h4>
                            </div>
                            <div className="form-group">
                                <label className='dashboard-form-label'>Venue</label>
                                <ClubSelector
                                    selectedClub={eventData.clubId ? {
                                        id: eventData.clubId,
                                        name: eventData.clubName
                                    } : null}
                                    onClubSelect={handleClubSelect}
                                />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="form-group">
                                <label className='dashboard-form-label'>
                                    <CalendarDays size={14} />
                                    Date
                                </label>
                                <div className="dashboard-form-field">
                                    <input
                                        type="date"
                                        name="eventDate"
                                        value={eventData.eventDate}
                                        onChange={handleInputChange}
                                        className='create-event-input'
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="form-group">
                                <label className='dashboard-form-label'>
                                    <Clock3 size={14} />
                                    Time
                                </label>
                                <div className="dashboard-form-field">
                                    <input
                                        type="time"
                                        name="eventTime"
                                        value={eventData.eventTime}
                                        onChange={handleInputChange}
                                        className='create-event-input'
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="create-event-next-step">
                        <Trophy size={16} />
                        <span>
                            Next step after create: we will take you to the {eventData.eventType.toLowerCase()} scoring area so you can add shooters and manage score sheets.
                        </span>
                    </div>

                    {formError && (
                        <div className="create-event-error">
                            {formError}
                        </div>
                    )}
                    <div className="modal-ctas">
                        <button type="button" className="dashboard-outline-btn me-2" onClick={closeModal}>
                            Cancel
                        </button>
                        <button type="submit" className="dashboard-default-btn" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create and continue'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateEvent;
